/**
 * Policy Pack Loader - Extract configurations from YAML policy packs
 * Makes report templates, entity types, and settings available at runtime
 */

import { ReportQuestion } from '../types/api';

export interface PolicyPackReportTemplate {
  id: string;
  name: string;
  description?: string;
  sections?: string[];
  questions?: ReportQuestion[];
  frequency?: string;
  deadline?: string;
  authorities?: string[];
  submissionTo?: string[];
}

export interface PolicyPackConfig {
  name: string;
  version: string;
  description: string;
  tenant_id: string;
  client_industry: string;
  jurisdiction?: string;
  
  report_templates?: Record<string, PolicyPackReportTemplate>;
  entity_types?: Record<string, any>;
  regulatory_frameworks?: any;
  compliance_rules?: any[];
  workflows?: Record<string, any>;
  
  // Cache config
  cache_config?: {
    query_cache_ttl_hours?: number;
    pre_warm_cache_queries?: string[];
  };
}

/**
 * Parse report templates from policy pack YAML structure
 */
export function extractReportTemplatesFromPolicyPack(policyPackData: any): PolicyPackReportTemplate[] {
  const templates: PolicyPackReportTemplate[] = [];
  
  // Check for reportTypes (TypeScript policy pack format)
  if (policyPackData.reportTypes && Array.isArray(policyPackData.reportTypes)) {
    policyPackData.reportTypes.forEach((rt: any) => {
      const template: PolicyPackReportTemplate = {
        id: rt.type || rt.id,
        name: rt.name,
        description: rt.description,
        frequency: rt.metadata?.frequency,
        deadline: rt.metadata?.deadline,
        authorities: rt.metadata?.authorities,
        questions: rt.defaultQuestions,
      };
      templates.push(template);
    });
  }
  
  // Check for report_types (alternative format)
  if (policyPackData.report_types && Array.isArray(policyPackData.report_types)) {
    policyPackData.report_types.forEach((rt: any) => {
      const template: PolicyPackReportTemplate = {
        id: rt.type || rt.id,
        name: rt.name,
        description: rt.description,
        frequency: rt.metadata?.frequency,
        deadline: rt.metadata?.deadline,
        authorities: rt.metadata?.authorities,
        questions: rt.defaultQuestions,
      };
      templates.push(template);
    });
  }
  
  // Check for report_templates section (YAML format)
  if (policyPackData.report_templates) {
    Object.entries(policyPackData.report_templates).forEach(([key, value]: [string, any]) => {
      const template: PolicyPackReportTemplate & { category?: string } = {
        id: key,
        name: value.name || key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
        description: value.description,
        sections: value.sections,
        frequency: value.frequency,
        deadline: value.deadline,
        authorities: value.authorities,
        submissionTo: value.submission_to || value.submissionTo,
        category: value.category, // Include category for icon/color mapping
      };
      
      // Convert sections to questions if questions not provided
      if (value.sections && !value.questions) {
        template.questions = value.sections.map((section: string) => ({
          section: section,
          question: `Provide comprehensive information for: ${section}`,
        }));
      } else if (value.questions) {
        template.questions = value.questions;
      }
      
      templates.push(template);
    });
  }
  
  // Check for workflows section (can also define report types)
  if (policyPackData.workflows) {
    Object.entries(policyPackData.workflows).forEach(([key, value]: [string, any]) => {
      // Check if this workflow defines a report
      if (value.steps || value.trigger) {
        const template: PolicyPackReportTemplate = {
          id: key,
          name: value.name || key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
          description: value.description || `${value.trigger} workflow`,
          frequency: value.trigger,
          deadline: value.due_date || value.due_dates?.join(', '),
        };
        
        // Convert steps to questions
        if (value.steps && Array.isArray(value.steps)) {
          template.questions = value.steps.map((step: string, idx: number) => ({
            section: `Step ${idx + 1}`,
            question: step,
          }));
        }
        
        // Only add if not already present
        if (!templates.find(t => t.id === key)) {
          templates.push(template);
        }
      }
    });
  }
  
  return templates;
}

/**
 * Get report type options for dropdown from policy pack
 */
export function getReportTypesFromPolicyPack(policyPackData: any): Array<{ value: string; label: string }> {
  const templates = extractReportTemplatesFromPolicyPack(policyPackData);
  
  const types = templates.map(t => ({
    value: t.id,
    label: t.name,
  }));
  
  // Add generic types as fallback
  if (types.length > 0) {
    types.unshift({ value: '', label: 'Select type...' });
  }
  types.push({ value: 'custom', label: 'Custom Report' });
  
  return types;
}

/**
 * Load policy pack from API or local config
 */
export async function loadPolicyPackConfig(policyPackId: string): Promise<PolicyPackConfig | null> {
  try {
    // In production, this would call: GET /policy/packs/{id}
    // For now, return null and fall back to hardcoded templates
    return null;
  } catch (error) {
    console.error('Failed to load policy pack:', error);
    return null;
  }
}

/**
 * Merge policy pack templates with hardcoded templates
 */
export function mergeReportTemplates(
  policyPackTemplates: PolicyPackReportTemplate[],
  hardcodedTemplates: any[]
): any[] {
  // Policy pack templates take precedence
  const merged = [...policyPackTemplates];
  
  // Add hardcoded templates that aren't in policy pack
  hardcodedTemplates.forEach(hardcoded => {
    if (!merged.find(t => t.id === hardcoded.id)) {
      merged.push(hardcoded);
    }
  });
  
  return merged;
}

/**
 * Generate report questions from policy pack workflow
 */
export function generateQuestionsFromWorkflow(workflow: any): ReportQuestion[] {
  const questions: ReportQuestion[] = [];
  
  if (workflow.steps && Array.isArray(workflow.steps)) {
    workflow.steps.forEach((step: string, idx: number) => {
      questions.push({
        section: `Step ${idx + 1}`,
        question: step,
      });
    });
  }
  
  return questions;
}

/**
 * Extract sample queries from policy pack
 * Supports both query_samples (YAML) and querySamples (TypeScript) formats
 */
export function getQuerySamplesFromPolicyPack(policyPackData: any): string[] {
  const queries: string[] = [];
  
  /**
   * Recursively extract queries from nested structure
   * Handles both flat arrays and nested objects with query categories
   * e.g., query_samples.requirement_extraction, query_samples.proposal_search
   */
  function extractQueriesRecursive(obj: any): void {
    if (!obj) return;
    
    if (Array.isArray(obj)) {
      // If it's an array, add all string items
      obj.forEach(item => {
        if (typeof item === 'string') {
          queries.push(item);
        } else if (typeof item === 'object') {
          extractQueriesRecursive(item);
        }
      });
    } else if (typeof obj === 'object') {
      // If it's an object, recursively process all values
      Object.values(obj).forEach(value => {
        extractQueriesRecursive(value);
      });
    }
  }
  
  // Check for query_samples (YAML format) - may be flat array or nested object
  if (policyPackData.query_samples) {
    extractQueriesRecursive(policyPackData.query_samples);
  }
  
  // Check for querySamples (TypeScript format) - may be flat array or nested object
  if (policyPackData.querySamples) {
    extractQueriesRecursive(policyPackData.querySamples);
  }
  
  // Also check in rules or policy_data if nested
  if (policyPackData.rules) {
    if (policyPackData.rules.query_samples) {
      extractQueriesRecursive(policyPackData.rules.query_samples);
    }
    if (policyPackData.rules.querySamples) {
      extractQueriesRecursive(policyPackData.rules.querySamples);
    }
  }
  
  if (policyPackData.policy_data) {
    if (policyPackData.policy_data.query_samples) {
      extractQueriesRecursive(policyPackData.policy_data.query_samples);
    }
    if (policyPackData.policy_data.querySamples) {
      extractQueriesRecursive(policyPackData.policy_data.querySamples);
    }
  }
  
  // Remove duplicates and limit to reasonable number
  const uniqueQueries = Array.from(new Set(queries));
  console.log(`[PolicyPackLoader] Extracted ${uniqueQueries.length} unique sample queries from policy pack`);
  
  return uniqueQueries.slice(0, 50); // Limit to top 50 queries
}

/**
 * Entity Type Configuration extracted from Policy Pack
 */
export interface PolicyPackEntityTypeConfig {
  id: string;
  label: string;
  entityType: string;
  keywords: string[];
  category?: string;
  enabled: boolean;
  displayName?: string;
  description?: string;
  fields?: string[];
  searchableFields?: string[];
}

/**
 * Extract entity types from policy pack YAML/JSON
 * Supports multiple formats:
 * - entity_extraction.entity_types (Keystone RFP format)
 * - entity_types (direct format)
 * - entityTypes (TypeScript format)
 */
export function getEntityTypesFromPolicyPack(policyPackData: any): PolicyPackEntityTypeConfig[] {
  const entityTypes: PolicyPackEntityTypeConfig[] = [];
  
  // Helper to convert entity type data to config
  const convertEntityTypeToConfig = (entityTypeKey: string, entityTypeData: any): PolicyPackEntityTypeConfig => {
    // Get display name
    const displayName = entityTypeData.displayName || entityTypeData.display_name || 
      entityTypeKey.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    
    // Get category
    const category = entityTypeData.category;
    
    // Get description
    const description = entityTypeData.description;
    
    // Get keywords - check multiple sources
    let keywords: string[] = [];
    
    // 1. From detection_keywords in entity_extraction section
    if (policyPackData.entity_extraction?.detection_keywords?.[entityTypeKey]) {
      keywords = [...policyPackData.entity_extraction.detection_keywords[entityTypeKey]];
    }
    
    // 2. From keywords array in entity type data
    if (entityTypeData.keywords && Array.isArray(entityTypeData.keywords)) {
      keywords.push(...entityTypeData.keywords);
    }
    
    // 3. From searchable_fields (add field names as keywords)
    if (entityTypeData.searchable_fields && Array.isArray(entityTypeData.searchable_fields)) {
      keywords.push(...entityTypeData.searchable_fields.map((f: string) => f.replace(/_/g, ' ')));
    } else if (entityTypeData.searchableFields && Array.isArray(entityTypeData.searchableFields)) {
      keywords.push(...entityTypeData.searchableFields.map((f: string) => f.replace(/_/g, ' ')));
    }
    
    // 4. Add the entity type key itself as a keyword
    keywords.push(entityTypeKey.replace(/_/g, ' '));
    
    // 5. Add the display name as keywords
    keywords.push(displayName.toLowerCase());
    
    // Remove duplicates and filter empty
    keywords = Array.from(new Set(keywords.filter(k => k && k.trim())));
    
    // Get searchable fields
    const searchableFields = entityTypeData.searchable_fields || entityTypeData.searchableFields || [];
    
    // Get all fields
    const fields = entityTypeData.fields || [];
    
    return {
      id: entityTypeKey,
      label: displayName,
      entityType: entityTypeKey,
      keywords,
      category,
      enabled: true,
      displayName,
      description,
      fields,
      searchableFields,
    };
  };
  
  // Format 1: entity_extraction.entity_types (Keystone RFP Agent v2 format)
  if (policyPackData.entity_extraction?.entity_types) {
    const entityTypesObj = policyPackData.entity_extraction.entity_types;
    Object.entries(entityTypesObj).forEach(([key, value]: [string, any]) => {
      entityTypes.push(convertEntityTypeToConfig(key, value));
    });
  }
  
  // Format 2: entity_types (direct format)
  if (policyPackData.entity_types && typeof policyPackData.entity_types === 'object') {
    const entityTypesObj = policyPackData.entity_types;
    Object.entries(entityTypesObj).forEach(([key, value]: [string, any]) => {
      if (!entityTypes.find(et => et.id === key)) {
        entityTypes.push(convertEntityTypeToConfig(key, value));
      }
    });
  }
  
  // Format 3: entityTypes (TypeScript format)
  if (policyPackData.entityTypes && typeof policyPackData.entityTypes === 'object') {
    const entityTypesObj = policyPackData.entityTypes;
    Object.entries(entityTypesObj).forEach(([key, value]: [string, any]) => {
      if (!entityTypes.find(et => et.id === key)) {
        entityTypes.push(convertEntityTypeToConfig(key, value));
      }
    });
  }
  
  // Format 4: Check in rules or policy_data if nested
  if (policyPackData.rules) {
    if (policyPackData.rules.entity_extraction?.entity_types) {
      const entityTypesObj = policyPackData.rules.entity_extraction.entity_types;
      Object.entries(entityTypesObj).forEach(([key, value]: [string, any]) => {
        if (!entityTypes.find(et => et.id === key)) {
          entityTypes.push(convertEntityTypeToConfig(key, value));
        }
      });
    }
    if (policyPackData.rules.entity_types && typeof policyPackData.rules.entity_types === 'object') {
      const entityTypesObj = policyPackData.rules.entity_types;
      Object.entries(entityTypesObj).forEach(([key, value]: [string, any]) => {
        if (!entityTypes.find(et => et.id === key)) {
          entityTypes.push(convertEntityTypeToConfig(key, value));
        }
      });
    }
  }
  
  if (policyPackData.policy_data) {
    if (policyPackData.policy_data.entity_extraction?.entity_types) {
      const entityTypesObj = policyPackData.policy_data.entity_extraction.entity_types;
      Object.entries(entityTypesObj).forEach(([key, value]: [string, any]) => {
        if (!entityTypes.find(et => et.id === key)) {
          entityTypes.push(convertEntityTypeToConfig(key, value));
        }
      });
    }
    if (policyPackData.policy_data.entity_types && typeof policyPackData.policy_data.entity_types === 'object') {
      const entityTypesObj = policyPackData.policy_data.entity_types;
      Object.entries(entityTypesObj).forEach(([key, value]: [string, any]) => {
        if (!entityTypes.find(et => et.id === key)) {
          entityTypes.push(convertEntityTypeToConfig(key, value));
        }
      });
    }
  }
  
  console.log(`[PolicyPackLoader] Extracted ${entityTypes.length} entity types from policy pack`);
  
  return entityTypes;
}

/**
 * Extract all service-line specific sample queries from policy pack
 * Used for query suggestions in search UI
 */
export function getServiceLineQueriesFromPolicyPack(policyPackData: any): Record<string, string[]> {
  const serviceLineQueries: Record<string, string[]> = {};
  
  // Check for query_samples.service_line_specific
  if (policyPackData.query_samples?.service_line_specific) {
    Object.entries(policyPackData.query_samples.service_line_specific).forEach(([key, value]: [string, any]) => {
      if (Array.isArray(value)) {
        serviceLineQueries[key] = value;
      }
    });
  }
  
  // Also check in rules/policy_data if nested
  if (policyPackData.rules?.query_samples?.service_line_specific) {
    Object.entries(policyPackData.rules.query_samples.service_line_specific).forEach(([key, value]: [string, any]) => {
      if (Array.isArray(value) && !serviceLineQueries[key]) {
        serviceLineQueries[key] = value;
      }
    });
  }
  
  return serviceLineQueries;
}

/**
 * Entity Governance Configuration Interface
 */
export interface EntityGovernanceConfig {
  quarantine_rules?: Array<{
    id: string;
    name: string;
    description: string;
    entity_types?: string[];
    source_types?: string[];
    action: 'quarantine' | 'reject';
    threshold?: number;
    keywords?: string[];
  }>;
  source_type_mapping?: {
    internal_document?: string[];
    external_government_opportunity?: string[];
    external_commercial_opportunity?: string[];
    unclassified?: string[];
  };
  enrichment?: {
    basic?: {
      enabled?: boolean;
      functions?: string[];
    };
    deep?: {
      enabled?: boolean;
      schedule?: string;
    };
    on_demand?: {
      enabled?: boolean;
    };
  };
  quality_thresholds?: {
    min_confidence?: number;
    min_completeness?: number;
  };
}

/**
 * Extract entity governance configuration from policy pack
 * Includes graceful degradation for policy packs without governance config
 * 
 * @param policyPackData - Policy pack data (from YAML or API)
 * @returns Entity governance config or null if not present
 */
export function getEntityGovernanceFromPolicyPack(policyPackData: any): EntityGovernanceConfig | null {
  if (!policyPackData) {
    return null;
  }
  
  // Check multiple possible locations (graceful degradation)
  let governanceConfig: any = null;
  
  // 1. Top-level entity_governance
  if (policyPackData.entity_governance) {
    governanceConfig = policyPackData.entity_governance;
  }
  
  // 2. Nested in rules
  if (!governanceConfig && policyPackData.rules?.entity_governance) {
    governanceConfig = policyPackData.rules.entity_governance;
  }
  
  // 3. Nested in policy_data
  if (!governanceConfig && policyPackData.policy_data?.entity_governance) {
    governanceConfig = policyPackData.policy_data.entity_governance;
  }
  
  // If still not found, return null (graceful degradation)
  if (!governanceConfig) {
    console.info('[PolicyPackLoader] No entity governance configuration found in policy pack');
    return null;
  }
  
  // Normalize and return
  const config: EntityGovernanceConfig = {
    quarantine_rules: governanceConfig.quarantine_rules || [],
    source_type_mapping: governanceConfig.source_type_mapping || {},
    enrichment: governanceConfig.enrichment || {},
    quality_thresholds: governanceConfig.quality_thresholds || {}
  };
  
  console.log(`[PolicyPackLoader] Loaded entity governance config with ${config.quarantine_rules?.length || 0} quarantine rules`);
  
  return config;
}

/**
 * Check if a policy pack has entity governance enabled
 * Used for feature detection / graceful degradation
 */
export function hasEntityGovernance(policyPackData: any): boolean {
  const config = getEntityGovernanceFromPolicyPack(policyPackData);
  return config !== null && (
    (config.quarantine_rules && config.quarantine_rules.length > 0) ||
    (config.source_type_mapping && Object.keys(config.source_type_mapping).length > 0)
  );
}

